import { useState, useCallback, useRef, useMemo } from 'react';
import sharedStateBridge from '@sendible/shared-state-bridge';
import { useTranslation } from 'react-i18next';
import { useGoogleApiLoader } from './useGoogleApiLoader';
import type { GoogleApiLoaderConfig, GoogleAuthToken } from './types';
import { GoogleAuthError, GoogleService } from './types';

const getStorageKey = (service: GoogleService) => `google_auth_token_${service}`;

const API_CONFIG = {
  baseUrl: `https://${WEBPACK_API}/api/v3`,
  endpoints: {
    googleOAuth: '/google/client_side_oauth',
  },
} as const;

const buildOAuthUrl = (service: GoogleService, accessToken: string, scopes: string[]): string => {
  const params = new URLSearchParams({
    service,
    access_token: accessToken,
    scope: scopes.join(' '),
  });

  return `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.googleOAuth}?${params.toString()}`;
};

const persistentTokens: Record<GoogleService, GoogleAuthToken | null> = (() => {
  const tokens: Record<GoogleService, GoogleAuthToken | null> = {} as Record<GoogleService, GoogleAuthToken | null>;

  Object.values(GoogleService).forEach((service) => {
    const storedToken = localStorage.getItem(getStorageKey(service));

    if (storedToken) {
      const parsedToken = JSON.parse(storedToken);
      const expiryTime = parsedToken.issued_at + parsedToken.expires_in * 1000;

      if (Date.now() > expiryTime) {
        localStorage.removeItem(getStorageKey(service));
        tokens[service] = null;
      } else {
        tokens[service] = parsedToken;
      }
    } else {
      tokens[service] = null;
    }
  });

  return tokens;
})();

export const useGoogleAuthenticate = () => {
  const [currentTokens, setCurrentTokens] = useState<Record<GoogleService, GoogleAuthToken | null>>(() => persistentTokens);
  const configRef = useRef<GoogleApiLoaderConfig | null>(null);
  const { loadGoogleApis } = useGoogleApiLoader();
  const { t } = useTranslation(['common_terms']);

  const initialize = useCallback(
    async (config: GoogleApiLoaderConfig): Promise<void> => {
      configRef.current = config;
      await loadGoogleApis(config);
    },
    [loadGoogleApis]
  );

  const updateToken = useCallback((token: GoogleAuthToken) => {
    if (!token.service) {
      throw new Error('Token must have a service property');
    }

    const tokenWithTimestamp = {
      ...token,
      issued_at: token.issued_at || Date.now(),
    };

    setCurrentTokens((prev) => ({
      ...prev,
      [token.service!]: tokenWithTimestamp,
    }));
    persistentTokens[token.service] = tokenWithTimestamp;
    localStorage.setItem(getStorageKey(token.service), JSON.stringify(tokenWithTimestamp));

    // Set the token in GAPI client if it's available
    // This ensures the token is set as soon as possible after authentication
    const setGapiToken = () => {
      if (window.gapi?.client) {
        window.gapi.client.setToken({
          access_token: tokenWithTimestamp.access_token,
        });

        return true;
      }

      return false;
    };

    if (!setGapiToken() && window.gapi) {
      window.gapi.load('client', () => {
        setGapiToken();
      });
    }
  }, []);

  const authenticateWithPopup = useCallback(
    (service: GoogleService): Promise<GoogleAuthToken> => {
      return new Promise((resolve, reject) => {
        if (!Object.values(GoogleService).includes(service)) {
          reject(new GoogleAuthError('Invalid service type provided'));

          return;
        }

        const { user } = sharedStateBridge.getSharedState();
        const { accessToken } = user;

        if (!accessToken) {
          reject(new GoogleAuthError('Access token not available'));

          return;
        }

        const serviceScopes = configRef.current?.serviceScopes?.[service] || configRef.current!.scopes;
        const url = buildOAuthUrl(service, accessToken, serviceScopes);

        const width = 800;
        const height = 500;
        const left = window.screen.width / 2 + window.screenX - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
          url,
          'google-cloud',
          `height=${height},width=${width},modal=yes,scrollbars=yes,alwaysRaised=yes,top=${top},left=${left}`
        );

        if (!popup) {
          reject(new GoogleAuthError(t('popup_blocked', { service: service || '' }) || 'Popup blocked', true));

          return;
        }

        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            reject(new GoogleAuthError(t('popup_closed_by_user') || 'Popup closed by user', true));
          }
        }, 1000);

        const messageHandler = (event: MessageEvent) => {
          const OAUTH_SUCCESS = 'oauth.google.succeeded';
          const OAUTH_FAILURE = 'oauth.google.failure';

          if (event.data && event.data.event === OAUTH_SUCCESS) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            popup.close();

            const authToken: GoogleAuthToken = {
              access_token: event.data.data.access_token,
              token_type: 'Bearer',
              expires_in: 3600,
              scope: event.data.data.scope || serviceScopes.join(' '),
              service,
              issued_at: Date.now(),
            };

            updateToken(authToken);
            resolve(authToken);
          } else if (event.data && event.data.event === OAUTH_FAILURE) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);

            popup.close();
            reject(new GoogleAuthError(`Google OAuth failed: ${event.data.data?.error_description || event.data.data?.error || 'Unknown error'}`));
          }
        };

        window.addEventListener('message', messageHandler);
      });
    },
    [t, updateToken]
  );

  const getToken = useCallback(
    (service?: GoogleService): GoogleAuthToken | null => {
      if (!service) {
        return Object.values(currentTokens).find((token) => token !== null) || null;
      }

      return currentTokens[service] || null;
    },
    [currentTokens]
  );

  const clearToken = useCallback((service: GoogleService): void => {
    setCurrentTokens((prev) => ({
      ...prev,
      [service]: null,
    }));
    persistentTokens[service] = null;
    localStorage.removeItem(getStorageKey(service));

    if (window.gapi?.client) {
      window.gapi.client.setToken(null);
    }
  }, []);

  const isAuthenticated = useCallback(
    (service: GoogleService): boolean => {
      const token = currentTokens[service];

      if (!token || !token.access_token) {
        return false;
      }

      const expiryTime = token.issued_at! + token.expires_in * 1000;

      if (Date.now() > expiryTime) {
        clearToken(service);

        return false;
      }

      return true;
    },
    [currentTokens, clearToken]
  );

  return useMemo(
    () => ({
      initialize,
      authenticateWithPopup,
      getToken,
      isAuthenticated,
      clearToken,
    }),
    [initialize, authenticateWithPopup, getToken, isAuthenticated, clearToken]
  );
};
